import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

type ConsentStatus = {
  enabled: boolean;
  loggingCurrentlyEnabled: boolean;
  updatedAt: string | null;
};

export function DeanTrainingConsentCard() {
  const { t } = useTranslation("dean");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading, isError } = useQuery<ConsentStatus>({
    queryKey: ["/api/dean/privacy/training-consent"],
    retry: false,
  });
  const mutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiRequest("PUT", "/api/dean/privacy/training-consent", { enabled });
      return response.json() as Promise<ConsentStatus>;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/dean/privacy/training-consent"] });
      toast({ title: t("trainingConsent.saved") });
    },
    onError: (error: Error) => {
      toast({
        title: t("trainingConsent.saveError"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Card data-testid="card-dean-training-consent">
      <CardHeader>
        <CardTitle>{t("trainingConsent.title")}</CardTitle>
        <CardDescription>
          {t("trainingConsent.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-5">
          <div className="space-y-1">
            <p className="font-medium">{t("trainingConsent.allowLogs")}</p>
            <p className="text-sm text-muted-foreground">
              {t("trainingConsent.loggingExplanation")}
            </p>
            {data?.enabled && !data.loggingCurrentlyEnabled && (
              <p className="text-sm text-muted-foreground" role="status">
                {t("trainingConsent.platformDisabled")}
              </p>
            )}
            {isError && (
              <p className="text-sm text-destructive" role="alert">
                {t("trainingConsent.loadError")}
              </p>
            )}
          </div>
          <Switch
            aria-label={t("trainingConsent.switchLabel")}
            data-testid="switch-dean-training-consent"
            checked={data?.enabled ?? false}
            disabled={isLoading || isError || mutation.isPending || !data}
            onCheckedChange={enabled => mutation.mutate(enabled)}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {data?.updatedAt ? t("trainingConsent.lastChanged", { date: new Date(data.updatedAt).toLocaleString() }) : t("trainingConsent.notRecorded")}
        </p>
      </CardContent>
    </Card>
  );
}